import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, Select, Tag, Space, message,
  Popconfirm, Descriptions, Badge, Tooltip, Tabs, Row, Col, Statistic, Typography,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, EditOutlined, ReloadOutlined,
  PrinterOutlined, RobotOutlined, CheckCircleOutlined, CloseCircleOutlined,
  ClockCircleOutlined, ExclamationCircleOutlined, CopyOutlined,
} from '@ant-design/icons';
import {
  agentService, printerService, printJobService,
  PrinterAgent, Printer, PrintJob, PrintStats,
  PrinterRole, PrintJobStatus,
} from '../../services/printerService';

const { Text } = Typography;

const ROLE_COLORS: Record<PrinterRole, string> = {
  RECEIPT: 'blue',
  KITCHEN: 'orange',
  LAUNDRY: 'purple',
  BAR: 'green',
};

const STATUS_COLORS: Record<PrintJobStatus, string> = {
  pending: 'default',
  dispatched: 'processing',
  printed: 'success',
  failed: 'error',
  timeout: 'warning',
  retrying: 'orange',
  dead: 'red',
};

const PrinterSettings: React.FC = () => {
  const [agents, setAgents] = useState<PrinterAgent[]>([]);
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [stats, setStats] = useState<PrintStats | null>(null);
  const [jobHistory, setJobHistory] = useState<PrintJob[]>([]);
  const [jobTotal, setJobTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('agents');

  // Agent modal
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState<PrinterAgent | null>(null);
  const [agentForm] = Form.useForm();
  const [newAgentToken, setNewAgentToken] = useState<string | null>(null);
  const [newAgentId, setNewAgentId] = useState<string | null>(null);

  // Printer modal
  const [printerModalOpen, setPrinterModalOpen] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState<Printer | null>(null);
  const [printerForm] = Form.useForm();

  // History filters
  const [historyFilters, setHistoryFilters] = useState<{
    status?: PrintJobStatus;
    printer_role?: PrinterRole;
  }>({});

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [agentsData, printersData, statsData] = await Promise.all([
        agentService.list(),
        printerService.list(),
        printJobService.getStats(),
      ]);
      setAgents(agentsData);
      setPrinters(printersData);
      setStats(statsData);
    } catch {
      message.error('Failed to load printer data');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const { jobs, total } = await printJobService.getHistory({ ...historyFilters, limit: 50 });
      setJobHistory(jobs);
      setJobTotal(total);
    } catch {
      message.error('Failed to load print history');
    }
  }, [historyFilters]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { if (activeTab === 'history') loadHistory(); }, [activeTab, loadHistory]);

  // =========================================================================
  // Agent CRUD
  // =========================================================================

  const handleCreateAgent = async (values: { name: string; location?: string }) => {
    try {
      const result = await agentService.create(values);
      setNewAgentToken(result.token);
      setNewAgentId(result.id);
      message.success('Agent registered');
      loadData();
    } catch {
      message.error('Failed to create agent');
    }
  };

  const handleUpdateAgent = async (values: { name: string; location?: string }) => {
    if (!editingAgent) return;
    try {
      await agentService.update(editingAgent.id, values);
      message.success('Agent updated');
      setAgentModalOpen(false);
      setEditingAgent(null);
      agentForm.resetFields();
      loadData();
    } catch {
      message.error('Failed to update agent');
    }
  };

  const handleDeleteAgent = async (id: string) => {
    try {
      await agentService.delete(id);
      message.success('Agent deleted');
      loadData();
    } catch {
      message.error('Failed to delete agent');
    }
  };

  const handleRegenerateToken = async (id: string) => {
    try {
      const { token } = await agentService.regenerateToken(id);
      setNewAgentToken(token);
      message.success('Token regenerated. Old token is now invalid.');
    } catch {
      message.error('Failed to regenerate token');
    }
  };

  // =========================================================================
  // Printer CRUD
  // =========================================================================

  const handleCreatePrinter = async (values: { agent_id: string; name: string; role: PrinterRole; paper_width_mm?: number }) => {
    try {
      await printerService.create(values);
      message.success('Printer registered');
      setPrinterModalOpen(false);
      printerForm.resetFields();
      loadData();
    } catch {
      message.error('Failed to create printer');
    }
  };

  const handleUpdatePrinter = async (values: { name?: string; role?: PrinterRole; is_active?: boolean }) => {
    if (!editingPrinter) return;
    try {
      await printerService.update(editingPrinter.id, values);
      message.success('Printer updated');
      setPrinterModalOpen(false);
      setEditingPrinter(null);
      printerForm.resetFields();
      loadData();
    } catch {
      message.error('Failed to update printer');
    }
  };

  const handleDeletePrinter = async (id: string) => {
    try {
      await printerService.delete(id);
      message.success('Printer deleted');
      loadData();
    } catch {
      message.error('Failed to delete printer');
    }
  };

  const handleRetryJob = async (jobId: string) => {
    try {
      await printJobService.retry(jobId);
      message.success('Job queued for retry');
      loadHistory();
    } catch {
      message.error('Failed to retry job');
    }
  };

  // =========================================================================
  // Agent Table Columns
  // =========================================================================

  const agentColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: PrinterAgent) => (
        <Space>
          <RobotOutlined />
          <Text strong>{name}</Text>
          {record.is_online ? (
            <Badge status="success" text={<Text type="success" style={{ fontSize: 12 }}>Online</Text>} />
          ) : (
            <Badge status="error" text={<Text type="danger" style={{ fontSize: 12 }}>Offline</Text>} />
          )}
        </Space>
      ),
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      render: (loc: string) => loc || '-',
    },
    {
      title: 'Printers',
      key: 'printers',
      render: (_: unknown, record: PrinterAgent) => (
        <Space wrap>
          {record.printers?.map((p) => (
            <Tag key={p.id} color={ROLE_COLORS[p.role]}>
              {p.name} ({p.role})
            </Tag>
          ))}
          {(!record.printers || record.printers.length === 0) && <Text type="secondary">No printers</Text>}
        </Space>
      ),
    },
    {
      title: 'Last Seen',
      dataIndex: 'last_heartbeat_at',
      key: 'last_heartbeat_at',
      render: (ts: string | null) => {
        if (!ts) return <Text type="secondary">Never</Text>;
        const d = new Date(ts);
        const ago = Math.round((Date.now() - d.getTime()) / 1000);
        if (ago < 60) return <Text type="success">{ago}s ago</Text>;
        if (ago < 3600) return <Text>{Math.round(ago / 60)}m ago</Text>;
        return <Text type="secondary">{d.toLocaleString()}</Text>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: PrinterAgent) => (
        <Space>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => {
              setEditingAgent(record);
              agentForm.setFieldsValue({ name: record.name, location: record.location });
              setAgentModalOpen(true);
            }} />
          </Tooltip>
          <Tooltip title="Regenerate Token">
            <Button size="small" icon={<ReloadOutlined />} onClick={() => handleRegenerateToken(record.id)} />
          </Tooltip>
          <Popconfirm title="Delete this agent?" onConfirm={() => handleDeleteAgent(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // =========================================================================
  // Printer Table Columns
  // =========================================================================

  const printerColumns = [
    {
      title: 'Printer',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => (
        <Space><PrinterOutlined /><Text strong>{name}</Text></Space>
      ),
    },
    {
      title: 'Printer ID',
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => (
        <Space>
          <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 11 }}>
            {id.substring(0, 8)}...
          </Text>
          <Button size="small" type="text" icon={<CopyOutlined />} onClick={() => {
            navigator.clipboard.writeText(id);
            message.success('Copied');
          }} />
        </Space>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role: PrinterRole) => <Tag color={ROLE_COLORS[role]}>{role}</Tag>,
    },
    {
      title: 'Agent',
      dataIndex: 'agent_name',
      key: 'agent_name',
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: unknown, record: Printer) => (
        record.agent_is_online
          ? <Badge status="success" text="Online" />
          : <Badge status="error" text="Offline" />
      ),
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active: boolean) => active
        ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
        : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: Printer) => (
        <Space>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => {
              setEditingPrinter(record);
              printerForm.setFieldsValue({ agent_id: record.agent_id, name: record.name, role: record.role, is_active: record.is_active });
              setPrinterModalOpen(true);
            }} />
          </Tooltip>
          <Popconfirm title="Delete this printer?" onConfirm={() => handleDeletePrinter(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // =========================================================================
  // Job History Table Columns
  // =========================================================================

  const jobColumns = [
    {
      title: 'Time',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (ts: string) => new Date(ts).toLocaleString(),
    },
    {
      title: 'Type',
      dataIndex: 'content_type',
      key: 'content_type',
      width: 110,
      render: (ct: string) => <Tag>{ct.toUpperCase()}</Tag>,
    },
    {
      title: 'Role',
      dataIndex: 'printer_role',
      key: 'printer_role',
      width: 100,
      render: (role: PrinterRole) => <Tag color={ROLE_COLORS[role]}>{role}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: PrintJobStatus) => <Tag color={STATUS_COLORS[status]}>{status.toUpperCase()}</Tag>,
    },
    {
      title: 'Attempts',
      dataIndex: 'attempts',
      key: 'attempts',
      width: 80,
    },
    {
      title: 'Error',
      dataIndex: 'last_error',
      key: 'last_error',
      ellipsis: true,
      render: (err: string | null) => err ? <Tooltip title={err}><Text type="danger">{err}</Text></Tooltip> : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_: unknown, record: PrintJob) => (
        ['dead', 'failed'].includes(record.status) ? (
          <Button size="small" onClick={() => handleRetryJob(record.id)}>Retry</Button>
        ) : null
      ),
    },
  ];

  // =========================================================================
  // Render
  // =========================================================================

  return (
    <div style={{ padding: 0 }}>
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
        {
          key: 'agents',
          label: <span><RobotOutlined /> Agents</span>,
          children: (
            <>
              <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">Register and manage Go Print Agent instances on each POS PC</Text>
                <Space>
                  <Button icon={<ReloadOutlined />} onClick={loadData}>Refresh</Button>
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                    setEditingAgent(null);
                    setNewAgentToken(null);
                    agentForm.resetFields();
                    setAgentModalOpen(true);
                  }}>Register Agent</Button>
                </Space>
              </div>
              <Table
                columns={agentColumns}
                dataSource={agents}
                rowKey="id"
                loading={loading}
                pagination={false}
              />
            </>
          ),
        },
        {
          key: 'printers',
          label: <span><PrinterOutlined /> Printers</span>,
          children: (
            <>
              <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">Map physical printers to logical roles (RECEIPT, KITCHEN, LAUNDRY, BAR)</Text>
                <Space>
                  <Button icon={<ReloadOutlined />} onClick={loadData}>Refresh</Button>
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                    setEditingPrinter(null);
                    printerForm.resetFields();
                    setPrinterModalOpen(true);
                  }}>Add Printer</Button>
                </Space>
              </div>
              <Table
                columns={printerColumns}
                dataSource={printers}
                rowKey="id"
                loading={loading}
                pagination={false}
              />
            </>
          ),
        },
        {
          key: 'history',
          label: <span><ClockCircleOutlined /> Print History</span>,
          children: (
            <>
              <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                  <Select placeholder="Status" allowClear style={{ width: 130 }} onChange={(v) => setHistoryFilters((f) => ({ ...f, status: v }))}>
                    {(['pending', 'dispatched', 'printed', 'failed', 'timeout', 'retrying', 'dead'] as PrintJobStatus[]).map((s) => (
                      <Select.Option key={s} value={s}>{s.toUpperCase()}</Select.Option>
                    ))}
                  </Select>
                  <Select placeholder="Role" allowClear style={{ width: 120 }} onChange={(v) => setHistoryFilters((f) => ({ ...f, printer_role: v }))}>
                    {(['RECEIPT', 'KITCHEN', 'LAUNDRY', 'BAR'] as PrinterRole[]).map((r) => (
                      <Select.Option key={r} value={r}>{r}</Select.Option>
                    ))}
                  </Select>
                </Space>
                <Button icon={<ReloadOutlined />} onClick={loadHistory}>Refresh</Button>
              </div>
              <Table
                columns={jobColumns}
                dataSource={jobHistory}
                rowKey="id"
                loading={loading}
                pagination={{ total: jobTotal, pageSize: 50 }}
              />
            </>
          ),
        },
        {
          key: 'stats',
          label: <span><ExclamationCircleOutlined /> Overview</span>,
          children: stats ? (
            <Row gutter={[16, 16]}>
              <Col span={4}><Card><Statistic title="Total Jobs" value={stats.total} /></Card></Col>
              <Col span={4}><Card><Statistic title="Pending" value={stats.pending} valueStyle={{ color: '#faad14' }} /></Card></Col>
              <Col span={4}><Card><Statistic title="Dispatched" value={stats.dispatched} valueStyle={{ color: '#1890ff' }} /></Card></Col>
              <Col span={4}><Card><Statistic title="Printed" value={stats.printed} valueStyle={{ color: '#52c41a' }} /></Card></Col>
              <Col span={4}><Card><Statistic title="Failed" value={stats.failed} valueStyle={{ color: '#ff4d4f' }} /></Card></Col>
              <Col span={4}><Card><Statistic title="Dead" value={stats.dead} valueStyle={{ color: '#ff4d4f' }} /></Card></Col>
            </Row>
          ) : <Text type="secondary">Loading stats...</Text>,
        },
      ]} />

      {/* Agent Modal */}
      <Modal
        title={editingAgent ? 'Edit Agent' : 'Register Agent'}
        open={agentModalOpen}
        onCancel={() => { setAgentModalOpen(false); setEditingAgent(null); setNewAgentToken(null); setNewAgentId(null); agentForm.resetFields(); }}
        footer={null}
        width={500}
      >
        {newAgentToken && (
          <Card size="small" style={{ marginBottom: 16, background: '#f6ffed', border: '1px solid #b7eb8f' }}>
            {newAgentId && (
              <>
                <Text strong>Agent ID:</Text>
                <div style={{ marginTop: 4, padding: 8, background: '#fff', borderRadius: 4, wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 12 }}>
                  {newAgentId}
                  <Button size="small" style={{ marginLeft: 8 }} icon={<CopyOutlined />} onClick={() => {
                    navigator.clipboard.writeText(newAgentId);
                    message.success('Copied');
                  }}>Copy</Button>
                </div>
              </>
            )}
            <Text strong style={{ marginTop: 8, display: 'block' }}>Agent Token (save this!):</Text>
            <div style={{ marginTop: 4, padding: 8, background: '#fff', borderRadius: 4, wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 12 }}>
              {newAgentToken}
              <Button size="small" style={{ marginLeft: 8 }} icon={<CopyOutlined />} onClick={() => {
                navigator.clipboard.writeText(newAgentToken);
                message.success('Copied');
              }}>Copy</Button>
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>Configure both values in config.yaml on the POS PC.</Text>
          </Card>
        )}
        <Form form={agentForm} layout="vertical" onFinish={editingAgent ? handleUpdateAgent : handleCreateAgent}>
          <Form.Item name="name" label="Agent Name" rules={[{ required: true, message: 'e.g. POS-PC-1' }]}>
            <Input placeholder="POS-PC-1" />
          </Form.Item>
          <Form.Item name="location" label="Location">
            <Input placeholder="Restaurant Counter" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>{editingAgent ? 'Update' : 'Register'}</Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Printer Modal */}
      <Modal
        title={editingPrinter ? 'Edit Printer' : 'Add Printer'}
        open={printerModalOpen}
        onCancel={() => { setPrinterModalOpen(false); setEditingPrinter(null); printerForm.resetFields(); }}
        footer={null}
        width={500}
      >
        <Form form={printerForm} layout="vertical" onFinish={editingPrinter ? handleUpdatePrinter : handleCreatePrinter}>
          {!editingPrinter && (
            <Form.Item name="agent_id" label="Agent" rules={[{ required: true }]}>
              <Select placeholder="Select agent">
                {agents.map((a) => (
                  <Select.Option key={a.id} value={a.id}>{a.name} {a.is_online ? '(Online)' : '(Offline)'}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item name="name" label="Printer Name (as shown in Windows)" rules={[{ required: true }]}>
            <Input placeholder="XPrinter XP-80C" />
          </Form.Item>
          <Form.Item name="role" label="Role" rules={[{ required: true }]}>
            <Select placeholder="Select role">
              {(['RECEIPT', 'KITCHEN', 'LAUNDRY', 'BAR'] as PrinterRole[]).map((r) => (
                <Select.Option key={r} value={r}>{r}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          {editingPrinter && (
            <Form.Item name="is_active" label="Active">
              <Select>
                <Select.Option value={true}>Active</Select.Option>
                <Select.Option value={false}>Inactive</Select.Option>
              </Select>
            </Form.Item>
          )}
          <Form.Item>
            <Button type="primary" htmlType="submit" block>{editingPrinter ? 'Update' : 'Add Printer'}</Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PrinterSettings;
